<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Move Datasets</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="section">
          <h3>Move to Project</h3>
          <select v-model="selectedProject" class="project-select">
            <option :value="null" disabled>Select a project...</option>
            <option v-for="project in availableProjects" :key="project.id" :value="project">
              {{ project.name }}
            </option>
          </select>
        </div>

        <div class="section">
          <h3>Selected Datasets</h3>
          <div class="datasets-list">
            <div v-for="dataset in selectedDatasets" :key="dataset.id" class="dataset-item">
              <div class="dataset-id">{{ toHex(dataset.id) }}</div>
              <div class="dataset-name">{{ dataset.name || 'Untitled' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-move"
          :disabled="!selectedProject || selectedDatasets.length === 0"
          @click="handleMove()"
        >
          Move
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const store = useStore();

const props = defineProps({
  selectedDatasets: {
    type: Array,
    default: () => [],
  },
  currentProjectId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['cancelled', 'move-datasets']);

const isOpen = ref(false);
const selectedProject = ref(null);
const projectList = ref([]);

const toHex = (id) => {
  return "d-" + id.toString(16);
};

const availableProjects = computed(() => {
  if (!projectList.value || !Array.isArray(projectList.value)) {
    return [];
  }
  return projectList.value.filter((project) => project.id !== props.currentProjectId);
});

const getProjectList = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.getProjectList();

    if (response && response.result) {
      projectList.value = response.result;
    } else {
      console.error('Failed to fetch projects:', response?.error);
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
  }
};

const openModal = () => {
  isOpen.value = true;
  selectedProject.value = null;
  getProjectList();
};

const closeModal = () => {
  isOpen.value = false;
  selectedProject.value = null;
};

const handleCancel = () => {
  closeModal();
  emit('cancelled');
};

const handleMove = async () => {
  if (!selectedProject.value) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'Please select a project to move to',
      type: 'warning',
      position: 'top',
    });
    return;
  }
  
  if (props.selectedDatasets.length === 0) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'No datasets selected to move',
      type: 'warning',
      position: 'top',
    });
    return;
  }
  
  try {
    const datasetIds = props.selectedDatasets.map(dataset => dataset.id);
    const projectId = selectedProject.value.id;
    
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api("dataset.move", {
      dataset_id: datasetIds,
      project_id: projectId,
    });

    if (response && !response.error) {
      emit('move-datasets', {
        datasets: props.selectedDatasets,
        targetProject: selectedProject.value,
        success: true,
      });
      closeModal();
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Successfully moved ${props.selectedDatasets.length} dataset(s)`,
        type: 'success',
        position: 'top',
      });
    } else {
      const errorMessage = response?.error?.message || 'Failed to move datasets';
      console.error('Failed to move datasets:', errorMessage);
      $toast.open({
        queue: true,
        dismissible: true,
        message: errorMessage,
        type: 'error',
        position: 'top',
      });
    }
  } catch (error) {
    const errorMessage = error?.message || 'Error moving datasets';
    console.error('Error moving datasets:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  }
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.datasets-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.dataset-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 13px;

  .dataset-id {
    font-weight: 600;
    color: #666;
    min-width: 80px;
  }

  .dataset-name {
    flex: 1;
    color: #333;
  }
}

.project-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
}

</style>
